import React, { useState, useCallback, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BootstrapCard from "../CarouselCard";

const CarouselT = ({ services, setBackgroundVideo, backgroundVideo }) => {
    const itemCount = services.length;
    const [activeIndex, setActiveIndex] = useState(Math.round(itemCount / 2 - 1));
    const [dragging, setDragging] = useState(false);
    const bar = useRef();
    const dragRef = useRef();

    const screenConfig = useMemo(() => {
        const screenWidth = window.innerWidth;
        if (screenWidth < 576) {
            return { baseSpacing: 50, spacingFactor: 100, offsetX: 180, visibleCards: 2 };
        } else if (screenWidth < 768) {
            return { baseSpacing: 10, spacingFactor: 210, offsetX: 180, visibleCards: 2 };
        } else if (screenWidth < 992) {
            return { baseSpacing: 50, spacingFactor: 200, offsetX: 180, visibleCards: 4 };
        } else if (screenWidth < 1200) {
            return { baseSpacing: 100, spacingFactor: 250, offsetX: 180, visibleCards: 6 };
        } else if (screenWidth < 1440) {
            return { baseSpacing: 250, spacingFactor: 100, offsetX: 180, visibleCards: 8 };
        } else if (screenWidth < 1600) {
            return { baseSpacing: 150, spacingFactor: 250, offsetX: 180, visibleCards: 8 };
        } else if (screenWidth < 1920) {
            return { baseSpacing: 450, spacingFactor: 150, offsetX: 180, visibleCards: 10 };
        } else {
            return { baseSpacing: 250, spacingFactor: 300, offsetX: 180, visibleCards: 8 };
        }
    }, []);

    const handleMouseClick = (e) => {
        setDragging(true);
        const progressBar = bar.current;
        if (!progressBar) return;

        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const newIndex = Math.floor((clickX / rect.width) * itemCount);

        setActiveIndex(newIndex);
        setDragging(false);
    };

    const handleMouseMove = useCallback((e) => {
        if (!dragging) return;

        const progressBar = bar.current;
        const rect = progressBar.getBoundingClientRect();
        const dragX = e.clientX - rect.left;
        const clampedX = Math.max(0, Math.min(rect.width, dragX));

        if (dragRef.current) {
            const position = `${(clampedX / rect.width) * 100}%`;
            dragRef.current.style.left = position;
            progressBar.style.setProperty("--pos-cursor", position);
        }

        if (Math.floor((clampedX / rect.width) * itemCount) !== activeIndex) {
            setActiveIndex(Math.floor((clampedX / rect.width) * itemCount));
        }
    }, [itemCount, dragging, activeIndex]);

    const handleCardClick = (index, event) => {
        event.stopPropagation();
        setActiveIndex(index);

        const progressBar = bar.current;
        if (progressBar) {
            const position = `${(index / itemCount) * 100}%`;
            progressBar.style.setProperty("--pos-cursor", position);
        }
    };

    const getItemStyle = useCallback((index) => {
        const { baseSpacing, spacingFactor, offsetX, visibleCards } = screenConfig;
        const position = index - activeIndex;
        const adjustedPosition = position < -Math.floor(itemCount / 2)
            ? position + itemCount
            : position > Math.floor(itemCount / 2)
                ? position - itemCount
                : position;

        const isVisible = Math.abs(adjustedPosition) <= Math.floor(visibleCards / 2);
        const zIndexBase = 1000;
        const zIndex = zIndexBase - Math.abs(adjustedPosition) * 10;

        // Scale factor calculation
        const scale = 1 - Math.abs(adjustedPosition) * 0.1;

        return {
            position: 'absolute',
            display: isVisible ? "block" : "none",
            left: `calc(50% - ${offsetX}px + ${adjustedPosition * (spacingFactor + baseSpacing)}px)`,
            // Separated transforms for better control
            transform: `scale(${scale}) translateY(-50%)`,
            top: '50%',
            marginTop: '-150px', // Half of your card height to center it
            zIndex,
            opacity: isVisible ? Math.max(0.4, 1 - Math.abs(adjustedPosition) * 0.3) : 0,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            willChange: "transform, opacity, left"
        };
    }, [activeIndex, itemCount, screenConfig]);
    const cardVariants = {
        hidden: {
            opacity: 0,
            scale: 0.8,
            transition: { duration: 0.3 }
        },
        visible: {
            opacity: 1,
            scale: 1,
            transition: { duration: 0.3 }
        }
    };

    return (
        <>
            <div className="video-background">
                <video
                    src={`https://api.glyphqai.com/uploads/backgroundvideo/${backgroundVideo}`}
                    autoPlay
                    muted
                    loop
                    playsInline
                    id="backgroundVideo"
                    className="w-full h-full object-cover"
                >
                    Your browser does not support the video tag.
                </video>
            </div>
            <div className="slider relative h-full" style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}>
                <AnimatePresence>
                    {services.map((data, index) => (
                        <motion.div
                            key={index}
                            className="item"
                            style={getItemStyle(index)}
                            initial="hidden"
                            animate="visible"
                            exit="hidden"
                            variants={cardVariants}
                            onClick={(event) => handleCardClick(index, event)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}         >
                            <BootstrapCard
                                imagen={data.image}
                                imagen1={data.image}
                                backgroundImage={`https://api.glyphqai.com/uploads/servicetype/${data.image}`}
                                btntext={data.title}
                                imageLink={"Service"}
                                image1Link={"Service"}
                                buttonLink={`Service?id=${data.id}`}
                                extraClass={`custom-class ${index === activeIndex ? 'active' : ''}`}
                                isActive={index === activeIndex} />
                        </motion.div>
                    ))}
                </AnimatePresence>
                <div
                    ref={bar}
                    className="progress-bar-container__"
                    onClick={handleMouseClick}
                    onMouseMove={handleMouseMove}
                >
                    <div
                        className="position-relative h-100 mx-auto"
                        style={{ width: "95%" }}
                    >
                        <motion.div
                            ref={dragRef}
                            className="draggable-circle__"
                            style={{
                                left: `${(activeIndex / (itemCount - 1)) * 100}%`,
                            }}
                            whileHover={{ scale: 1.2 }}
                            whileTap={{ scale: 0.9 }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default CarouselT;
